import React, { useState, useEffect, useCallback, useContext } from "react";
import DBLayout from "../../../components/molecules/DashboardLayout/DBLayout";
import { AppBar, Tab, Tabs } from "@material-ui/core";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import { useFormik } from "formik";
import PrimaryButton from "../../atoms/PrimaryButton";
import { useDispatch, useSelector } from "react-redux";
import {
  getMyAuctions,
  updateAuctionsData,
} from "../../../store/slices/auctions";
import MyAuctionCard from "../../atoms/ListingCard/myAuctionCard";
import Loader from "../../atoms/Loader";
import socket from "../../../utils/socket";
import GlobalContext from "../../../context/GlobalContext";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: theme.spacing(0),
    marginTop: theme.spacing(5),
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3} className={classes.root}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const MyAuction = (props) => {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);
  const [step, setStep] = React.useState(1);
  const dispatch = useDispatch();
  const auctions = useSelector((state) => state.auctions?.auctionsData);
  const [final, setFinal] = React.useState(false);
  const loading = useSelector((state) => state.auctions?.loading);
  const [currentTab, setCurrentTab] = useState("won_history");
  const { userDetails } = useContext(GlobalContext);

  const handleChange = (event, newValue) => {
    if (step === 1) {
      setValue(newValue);
      setCurrentTab(newValue === 0 ? "won_history" : "lost_history");
    }
  };

  useEffect(() => {
    if (auctions.length == (undefined || null || 0)) {
      dispatch(getMyAuctions({ action: "won_history" }));
    }
  }, [dispatch]);

  // Handle real-time updates from socket
  const handleBidUpdate = useCallback(
    (data) => {
      const loggedInUserId = userDetails.get["id"]
        ? parseInt(userDetails.get["id"], 10)
        : null;

      // Get the product ID from the socket data
      const productId = parseInt(data.productId, 10);

      // Get the current user's ID from the socket data
      const currentBidderId = parseInt(data.userId, 10);
      const outBidderId = parseInt(data.outbiddedUser, 10);

      // Check if the current logged-in user is the highest bidder
      const isUserHighestBidder = currentBidderId === loggedInUserId;
      const isOutBidder = outBidderId === loggedInUserId;

      if (isOutBidder) {
        toast.info("You are no longer the highest bidder", {
          preventDuplicate: true,
        });
      }

      if (isUserHighestBidder) {
        if (data.message) {
          toast.info(data.message, {
            preventDuplicate: true,
          });
        }
        toast.success("Bid placed successfully!", {
          preventDuplicate: true,
        });
      }

      // Check if the updated product is in our current auctions list
      const auctionIndex = auctions.findIndex(
        (auction) => auction.id === productId,
      );

      // If the product isn't in our list but matches the user, refresh the list
      if (auctionIndex === -1) {
        if (isUserHighestBidder || isOutBidder) {
          // If the logged-in user is now the highest bidder or outbidder but the product isn't in the list,
          // refresh the tab
          dispatch(getMyAuctions({ action: currentTab }));
          return;
        }
        return; // Not found and not relevant to this user
      }

      // Create a copy of the auctions array
      const updatedAuctions = [...auctions];

      if (
        (currentTab === "won_history" && !isUserHighestBidder) ||
        (currentTab === "lost_history" && isUserHighestBidder)
      ) {
        // Remove the item from the list if it no longer belongs in the current tab
        updatedAuctions.splice(auctionIndex, 1);
        dispatch(updateAuctionsData(updatedAuctions));
      } else {
        // Update the item data if it stays in the current tab
        const updatedItem = { ...updatedAuctions[auctionIndex] };

        // Update relevant fields
        updatedItem.bidcount = data.bidCount;
        updatedItem.wprice = data.wPrice;
        updatedItem.bidCount = data.bidCount;

        if (Number(data.belowFive) === 1) {
          updatedItem.date_closed = data.belowFiveIncrement;
        }

        updatedAuctions[auctionIndex] = updatedItem;
        dispatch(updateAuctionsData(updatedAuctions));
      }
    },
    [auctions, currentTab, dispatch, userDetails],
  );

  useEffect(() => {
    // Set up socket listener
    socket.on("bidAddtime", handleBidUpdate);

    // Cleanup socket listener
    return () => {
      socket.off("bidAddtime", handleBidUpdate);
    };
  }, [handleBidUpdate]);

  const formik = useFormik({
    initialValues: {
      sell_type: "",
    },
    onSubmit: (values) => {
      setFinal(true);
    },
  });

  const submitFunction = (data) => {
    const updatedValues = {
      ...formik.values,
      ...data,
    };
    formik.setValues(updatedValues);
    formik.handleSubmit();
  };

  return (
    <DBLayout>
      <div className="clearfix db-mt-35">
        <h2 className="pp-head float-left">My Auctions</h2>
      </div>
      <div className="tabCustWidth">
        <AppBar position="static" color="inherit">
          <Tabs
            TabIndicatorProps={{
              style: { display: "none" },
            }}
            value={value}
            onChange={handleChange}
            aria-label="simple tabs example"
          >
            <Tab
              key={0}
              label="Winning"
              {...a11yProps(0)}
              onClick={() => dispatch(getMyAuctions({ action: "won_history" }))}
            />
            <Tab
              key={1}
              label="Losing"
              {...a11yProps(1)}
              onClick={() =>
                dispatch(getMyAuctions({ action: "lost_history" }))
              }
            />
            {/* <Tab key={2} label="Watchlist" {...a11yProps(2)} /> */}
          </Tabs>
        </AppBar>
      </div>
      <TabPanel value={value} index={0} className={classes.root}>
        {loading ? (
          <div className="card card-body text-center d-flex align-items-center w-100">
            <Loader />
          </div>
        ) : auctions.length > 0 ? (
          auctions.map((item, index) => {
            return (
              <MyAuctionCard item={item} action="won_history" index={index} />
            );
          })
        ) : (
          <div className="card card-body text-center">No Listings Found</div>
        )}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {loading ? (
          <div className="card card-body text-center d-flex align-items-center w-100">
            <Loader />
          </div>
        ) : auctions.length > 0 ? (
          auctions.map((item, index) => {
            return (
              <MyAuctionCard item={item} action="lost_history" index={index} />
            );
          })
        ) : (
          <div className="card card-body text-center">No Listings Found</div>
        )}
      </TabPanel>
      {/* <TabPanel value={value} index={2}>
        <WatchlistList />
      </TabPanel> */}
    </DBLayout>
  );
};

export default MyAuction;
